import { AwsS3, DragDrop, FileInput, Informer, Uppy } from 'uppy';
import i18n from '../init/i18n';

import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = [
    'input',
    'result',
    'fileName',
    'link',
    'emptyField',
    'filledField',
    'fileInputsContainer',
    'fileInputTemplate',
  ];

  static values = {
    allowedTypes: Array,
    multiple: Boolean
  };

  connect() {
    this.uploadedFilesCount = this.inputTarget.files.length;
    this.pendingFileIds = new Set();
    this.#toggleValidity();
    this.inputTarget.style.display = 'none';

    this.uppy = this.createUppy();
    if (this.#hasDocumentName() || this.inputTarget.files[0]) {
      this.showFileNameAndFilledIcon();
    }
  }

  disconnect() {
    this.uppy.close();
  }

  createUppy() {
    const uppy = new Uppy({
      autoProceed: true,
      restrictions: {
        allowedFileTypes: this.allowedTypesValue,
      },
      locale: {
        strings: {
          youCanOnlyUploadFileTypes: 'Le format du fichier uploadé n\'est pas autorisé',
        },
      },
    })
      .use(AwsS3, {
        companionUrl: '/',
      })
      .use(FileInput, {
        target: this.inputTarget.parentNode,
      })
      .use(Informer, {
        target: this.inputTarget.parentNode,
      })
      .use(DragDrop, { target: this.inputTarget.parentNode });

    uppy.on('upload-success', (file, response) => {
      const uploadedFileData = {
        id: file.meta.key.match(/^cache\/(.+)/)[1],
        storage: 'cache',
        metadata: {
          size: file.size,
          filename: file.name,
          mime_type: file.type,
        },
      };

      // set hidden field value to the uploaded file data so that it's submitted with the form as the attachment
      if (this.multipleValue === true) {
        const field = this.#getFileField(file.id);
        if (field) field.value = JSON.stringify(uploadedFileData);
        this.pendingFileIds.delete(file.id);
        this.#toggleSubmit();
      } else {
        this.resultTarget.value = JSON.stringify(uploadedFileData);
      }
    });

    uppy.on('file-added', (file) => {
      this.uploadedFilesCount += 1;

      this.#toggleValidity();

      if (this.multipleValue === true) {
        this.#addFileInput(file);
        this.pendingFileIds.add(file.id);
        this.#toggleSubmit();
      } else {
        this.showFileNameAndFilledIcon(file);
      }
    });

    uppy.on('upload-error', (file) => {
      if (this.multipleValue === true) {
        this.pendingFileIds.delete(file.id);
        const fileInput = this.#getFileField(file.id);
        if (fileInput) fileInput.closest('.file-input-wrapper').remove();
        this.uploadedFilesCount -= 1;
        this.#toggleValidity();
        this.#toggleSubmit();
      }
    });

    return uppy;
  }

  #hasDocumentName() {
    return this.fileNameTarget.innerText.trim().length > 0;
  }

  showFileNameAndFilledIcon(file) {
    this.emptyFieldTarget.classList.add('d-none');
    this.filledFieldTarget.classList.remove('d-none');
    if (file) {
      this.fileNameTarget.innerHTML = file.name;
    }
  }

  removeFileInput(event) {
    const fileId = event.currentTarget.dataset.fileId;
    const fileInput = this.#getFileField(fileId);

    this.pendingFileIds.delete(fileId);
    this.uppy.removeFile(fileId);

    this.uploadedFilesCount -= 1;
    this.#toggleValidity();
    this.#toggleSubmit();

    if (fileInput) {
      fileInput.closest('.file-input-wrapper').remove();
    }
  }

  #getFileField(fileId) {
    return this.fileInputsContainerTarget.querySelector(`input[data-file-id='${fileId}']`);
  }

  #addFileInput(file) {
    const content = this.fileInputTemplateTarget.innerHTML.replace(
      /FILE_ID/g,
      file.id
    ).replace(
      /FILE_NAME/g,
      file.name
    );

    this.fileInputsContainerTarget.insertAdjacentHTML('beforeend', content);
  }

  #toggleSubmit() {
    const form = this.element.closest('form');
    if (!form) return;

    const submitBtn = form.querySelector('[type="submit"]');
    if (!submitBtn) return;

    submitBtn.disabled = this.pendingFileIds.size > 0;
  }

  #toggleValidity() {
    if (this.uploadedFilesCount > 0) {
      this.inputTarget.setCustomValidity('');
    } else {
      this.inputTarget.setCustomValidity(i18n.t('file_input_error'));
    }
  }
}
